import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Mail, Copy, Trash2, Globe } from 'lucide-react';
import { newsletterApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import NewsletterEditor, { Results } from './NewsletterEditor';
import NewsletterContactsPanel from './NewsletterContactsPanel';

const SUBTABS = [['campaigns', 'Newsletters'], ['contacts', 'Newsletter Contacts'], ['settings', 'Settings & sign-up link']];
const statusLabel = (a) => (a.status === 'draft' ? 'Not sent' : a.status === 'sending' ? 'Sending' : `Sent ${a.startedAt ? new Date(a.startedAt).toLocaleDateString() : ''}`);

function CampaignList({ onOpen }) {
  const [items, setItems] = useState(null);
  const load = () => newsletterApi.list().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const del = async (c) => {
    if (!window.confirm(`Delete draft "${c.name}"?`)) return;
    try { await newsletterApi.remove(c.id); load(); } catch (e) { toast({ title: 'Delete failed', description: e?.response?.data?.detail }); }
  };
  return (
    <div className="space-y-3" data-testid="nl-campaign-list">
      <button onClick={() => onOpen(null)} className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold inline-flex items-center gap-1" data-testid="nl-new-btn"><Plus size={14} /> New newsletter</button>
      {items === null ? <Loader2 className="animate-spin text-gray-400" /> : items.length === 0 ? <div className="pbb-card p-6 text-sm text-gray-300" data-testid="nl-empty">No newsletters yet. Start your first welcome newsletter.</div>
        : items.map(c => (
          <div key={c.id} className="pbb-card p-4 space-y-3" data-testid={`nl-campaign-${c.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <div className="font-semibold text-white">{c.name}</div>
              {c.publishedAt && <span className="text-[10px] px-2 py-0.5 rounded bg-green-500/15 text-green-300 inline-flex items-center gap-1"><Globe size={10} /> In app</span>}
              <span className="text-xs text-gray-400">Created {new Date(c.createdAt).toLocaleDateString()}</span>
              <div className="ml-auto flex gap-2">
                <button onClick={() => onOpen(c)} className="px-3 py-1.5 rounded-lg border border-[#2a2a2a] text-sm text-gray-100" data-testid={`nl-open-${c.id}`}>Open</button>
                {c.members.status === 'draft' && c.contacts.status === 'draft' && <button onClick={() => del(c)} className="p-1.5 text-red-300" data-testid={`nl-delete-${c.id}`}><Trash2 size={14} /></button>}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {[['members', 'Members'], ['contacts', 'Contacts']].map(([k, l]) => (
                <div key={k} className="bg-black/20 rounded-lg p-3 space-y-2" data-testid={`nl-result-${c.id}-${k}`}>
                  <div className="text-xs text-gray-300"><b className="text-white">{l}</b> · {statusLabel(c[k])}</div>
                  {c[k].status !== 'draft' ? <Results a={c[k]} /> : <div className="text-[11px] text-gray-400">Open the newsletter to preview and send.</div>}
                </div>
              ))}
            </div>
            {c.buttons?.length > 0 && (
              <div className="text-xs text-gray-300" data-testid={`nl-clicks-${c.id}`}>
                <b className="text-white">Button clicks:</b>{' '}
                {c.buttons.map(b => { const k = c.clicks?.[b.id] || {}; return `${b.label || 'Picture button'}${b.sponsor ? ' (Sponsor)' : ''}: ${(k.email || 0) + (k.app || 0)} (email ${k.email || 0}, app ${k.app || 0})`; }).join(' · ')}
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

function SettingsPanel() {
  const [s, setS] = useState(null);
  const [limit, setLimit] = useState('');
  const [testEmail, setTestEmail] = useState('');
  useEffect(() => { newsletterApi.settings().then(d => { setS(d); setLimit(String(d.dailyLimit)); setTestEmail(d.testEmail || ''); }); }, []);
  if (!s) return <Loader2 className="animate-spin text-gray-400" />;
  const save = async () => {
    try { const d = await newsletterApi.saveSettings({ dailyLimit: parseInt(limit, 10), testEmail }); setS({ ...s, ...d }); toast({ title: 'Saved' }); }
    catch (e) { const dt = e?.response?.data?.detail; toast({ title: 'Save failed', description: typeof dt === 'string' ? dt : 'Use a daily limit from 1 to 50000' }); }
  };
  const copy = () => { navigator.clipboard.writeText(s.signupUrl); toast({ title: 'Sign-up link copied' }); };
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="nl-settings">
      <div className="pbb-card p-4 space-y-2 text-sm">
        <div className="font-semibold text-white">Email service</div>
        <div className="text-gray-200">Status: <b className={s.emailReady ? 'text-green-300' : 'text-red-300'} data-testid="nl-email-ready">{s.emailReady ? 'Connected (Resend)' : 'Not connected'}</b></div>
        <div className="text-gray-200">Sender: <b className="text-white" data-testid="nl-sender">{s.sender}</b></div>
        <div className="text-gray-200">Do-not-email list: <b className="text-white">{s.suppressed}</b> address(es)</div>
        <label className="block text-gray-200 pt-2">Test emails go to
          <input value={testEmail} onChange={e => setTestEmail(e.target.value)} className="pbb-input mt-1" data-testid="nl-test-email" />
        </label>
        <label className="block text-gray-200 pt-2">Newsletter emails per day
          <div className="flex gap-2 mt-1">
            <input value={limit} onChange={e => setLimit(e.target.value.replace(/\D/g, ''))} className="pbb-input !w-28" data-testid="nl-daily-limit" />
            <button onClick={save} className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold" data-testid="nl-daily-limit-save">Save</button>
          </div>
        </label>
        <p className="text-xs text-gray-400">Resend's free plan allows 100 emails a day (3,000 a month), shared with the app's other emails such as welcomes and password resets. 80 leaves room for those. Anything over the limit is sent automatically at 9am ET the next day. If you upgrade Resend (Pro is $20/month for 50,000 emails), raise this number.</p>
      </div>
      <div className="pbb-card p-4 space-y-2 text-sm">
        <div className="font-semibold text-white">Shareable sign-up link</div>
        <div className="flex gap-2">
          <input readOnly value={s.signupUrl} className="pbb-input" data-testid="nl-signup-url" />
          <button onClick={copy} className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-gray-100 inline-flex items-center gap-1" data-testid="nl-copy-signup"><Copy size={14} /> Copy</button>
        </div>
        <p className="text-xs text-gray-400">Share it by text, social media or at events. People who sign up confirm their email first, then they're subscribed.</p>
      </div>
    </div>
  );
}

export default function AdminNewsletterPanel({ initialTab = 'campaigns' }) {
  const [tab, setTab] = useState(initialTab);
  const [editing, setEditing] = useState(undefined);
  return (
    <div className="space-y-4" data-testid="admin-newsletter">
      <div className="flex items-center gap-2"><Mail size={18} className="text-green-400" /><h2 className="text-lg font-semibold text-white">Newsletter</h2></div>
      {editing === undefined && (
        <div className="flex flex-wrap gap-1">
          {SUBTABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`nl-subtab-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${tab === k ? 'bg-green-500 text-black border-green-500' : 'border-[#2a2a2a] text-gray-200'}`}>{l}</button>
          ))}
        </div>
      )}
      {editing !== undefined ? <NewsletterEditor campaign={editing} onClose={() => setEditing(undefined)} />
        : tab === 'campaigns' ? <CampaignList onOpen={setEditing} />
        : tab === 'contacts' ? <NewsletterContactsPanel /> : <SettingsPanel />}
    </div>
  );
}
